import {
  InspectorControlsAppearance,
  InspectorControlsGeneral,
  InspectorTabs,
} from '@components/inspector-tabs';
import {
  store as blockEditorStore,
  __experimentalBlockVariationPicker as BlockVariationPicker,
} from '@wordpress/block-editor';
import {
  ToggleControl,
  PanelBody,
  SelectControl,
  __experimentalNumberControl as NumberControl,
} from '@wordpress/components';
import { DEFAULT_CSS_CLASS } from '@constants';
import Visibility from '@components/visibility';
import variations from './variations';
import ClassDropdown, { getBlockClassNames } from '@components/class-dropdown';
import { createBlocksFromInnerBlocksTemplate } from '@wordpress/blocks';
import Label from '@components/block-label';
import {
  floatPanelDefaultBreakPoints,
  cloneChildBlockByParentClientId,
} from '@helpers/utils.js';
import { title } from './block.json';
import {
  FocusableIcon,
  FocusableIconSwitcher,
} from '@components/focusable-block-icon';

import { accordionIcon } from './icons';
import classnames from 'classnames';
import './editor.scss';

const {
  i18n: { __ },
  blockEditor: {
    useBlockProps,
    useInnerBlocksProps,
    InspectorControls,
    useSetting,
  },
  element: { useEffect },
  data: { useDispatch, withSelect },
} = wp;

const ALLOWED_BLOCKS = ['b3rg/accordion-item', 'b3rg/accordion-top-content'];
const BLOCKS_TEMPLATE = [['b3rg/accordion-item', { expandDefault: true }]];

const Placeholder = ({ clientId, setAttributes }) => {
  const { replaceInnerBlocks } = useDispatch(blockEditorStore);
  // when skip, first variant will be set as a default
  const defaultVariation = variations[0];

  return (
    <BlockVariationPicker
      variations={variations}
      label={__('Accordion', 'b3rg')}
      instructions={__('Choose a variation to start with.', 'b3rg')}
      icon={accordionIcon}
      onSelect={(variation = defaultVariation) => {
        if (variation.name === 'float-panel-accordion') {
          setAttributes({ isFloatPanelVariant: true, floatPanel: true });
        }
        if (variation.attributes) {
          setAttributes(variation.attributes);
        }
        if (variation.innerBlocks) {
          replaceInnerBlocks(
            clientId,
            createBlocksFromInnerBlocksTemplate(variation.innerBlocks),
            true,
          );
        }
      }}
      allowSkip
    />
  );
};

const EditContainer = ({ name, clientId, attributes, setAttributes }) => {
  const {
    blockVisibility,
    blockClassNames,
    expandAll,
    toggleMode,
    colBreakpoint,
    floatPanel,
    autoSlide,
    autoPlayInterval,
    floatPanelRowReverse,
    pauseOnFocus,
    label,
    isFloatPanelVariant,
    enableFocusableIcon,
  } = attributes;

  const classNames = getBlockClassNames(blockClassNames);
  const defaultBlockCssClass = DEFAULT_CSS_CLASS.ACCORDION;

  useEffect(() => {
    setAttributes({
      toggleMode: floatPanel ? true : toggleMode,
      expandAll: autoSlide ? false : expandAll,
    });
  }, [toggleMode, floatPanel, autoSlide, expandAll, setAttributes]);

  const accordionRootClasses = classnames({
    [`${defaultBlockCssClass}`]: true,
    [`${classNames}`]: classNames,
    'has-float-panel': floatPanel,
    'row-reverse': floatPanelRowReverse,
    'auto-slide': autoSlide,
    'has-focusable-icon': enableFocusableIcon,
    disable: !blockVisibility,
  });

  const blockProps = useBlockProps();

  const { children, ...innerBlocksProps } = useInnerBlocksProps(blockProps, {
    allowedBlocks: ALLOWED_BLOCKS,
    template: BLOCKS_TEMPLATE,
    renderAppender: false,
  });
  const floatPanelBreakPoints =
    useSetting(`custom.blocks.${name}.floatPanelStackBreakPoints`) ??
    floatPanelDefaultBreakPoints;

  const enableAutoSlideSetting =
    useSetting(`custom.blocks.${name}.autoSlide`) ?? false;

  const dataSettings = {
    forceExpand: expandAll,
    floatPanel,
    toggleMode,
  };

  const addEmptyItem = () => {
    const innerBlocks = wp.data
      .select('core/block-editor')
      .getBlockOrder(clientId);

    wp.data
      .dispatch('core/block-editor')
      .insertBlock(
        wp.blocks.createBlock('b3rg/accordion-item', {}),
        innerBlocks?.length,
        clientId,
      );
  };

  const addTopContentBlock = () => {
    wp.data
      .dispatch('core/block-editor')
      .insertBlock(
        wp.blocks.createBlock('b3rg/accordion-top-content', {}),
        0,
        clientId,
      );
  };

  return (
    <>
      <InspectorControls>
        <InspectorTabs>
          <InspectorControlsAppearance>
            <ClassDropdown
              {...{
                blockClassNames,
                setAttributes,
                blockClassPrefix: DEFAULT_CSS_CLASS.ACCORDION,
                initialOpen: true,
              }}
            />
            <FocusableIconSwitcher
              {...{
                enableFocusableIcon,
                setAttributes,
              }}
            />
          </InspectorControlsAppearance>
          <InspectorControlsGeneral>
            <Label {...{ label, setAttributes }} />
            <Visibility {...{ blockVisibility, setAttributes }} />
            <PanelBody title={__('Accordion Settings', 'b3rg')}>
              <ToggleControl
                label={__('Forced Expand All')}
                checked={expandAll}
                disabled={autoSlide}
                onChange={(newValue) => {
                  setAttributes({
                    expandAll: newValue,
                  });
                }}
              />
              {!isFloatPanelVariant && (
                <ToggleControl
                  label={__('Auto Shrink')}
                  checked={toggleMode}
                  onChange={(newValue) => {
                    setAttributes({
                      toggleMode: newValue,
                    });
                  }}
                />
              )}

              {isFloatPanelVariant && (
                <>
                  <ToggleControl
                    label={__('Flip Float Panels', 'b3rg')}
                    checked={floatPanelRowReverse}
                    onChange={(newValue) => {
                      setAttributes({
                        floatPanelRowReverse: newValue,
                      });
                    }}
                  />
                  {/* Auto slide feature can be enabled only from theme setting  */}
                  {enableAutoSlideSetting && (
                    <>
                      <ToggleControl
                        label={__('Auto Play', 'b3rg')}
                        checked={autoSlide}
                        onChange={(newValue) => {
                          setAttributes({
                            autoSlide: newValue,
                          });
                        }}
                      />
                      {autoSlide && (
                        <>
                          <ToggleControl
                            label={__('Pause on Focus', 'b3rg')}
                            checked={pauseOnFocus}
                            onChange={(newValue) => {
                              setAttributes({
                                pauseOnFocus: newValue,
                              });
                            }}
                          />
                          <NumberControl
                            label={__('Auto Play Speed', 'b3rg')}
                            isShiftStepEnabled={true}
                            onChange={(newValue) => {
                              if (newValue) {
                                setAttributes({
                                  autoPlayInterval: parseInt(newValue),
                                });
                              }
                            }}
                            shiftStep={1}
                            value={autoPlayInterval}
                          />
                        </>
                      )}
                    </>
                  )}
                  <SelectControl
                    label={__('Single column breakpoint', 'b3rg')}
                    value={colBreakpoint}
                    options={floatPanelBreakPoints}
                    onChange={(breakpoint) =>
                      setAttributes({ colBreakpoint: Number(breakpoint) })
                    }
                  />
                </>
              )}
            </PanelBody>
          </InspectorControlsGeneral>
        </InspectorTabs>
      </InspectorControls>
      <div
        className={accordionRootClasses}
        data-accordion={true}
        data-settings={JSON.stringify(dataSettings)}
      >
        <div {...innerBlocksProps}>
          {enableFocusableIcon && (
            <FocusableIcon {...{ title, icon: accordionIcon }} />
          )}
          {children}
          <div className="custom-appender-btn-group">
            <button
              className="custom-appender-btn clone-last-item"
              onClick={() => cloneChildBlockByParentClientId(clientId)}
            >
              Clone Last Item
            </button>
            <button
              className="custom-appender-btn new-acc-item"
              onClick={() => addEmptyItem()}
            >
              New Accordion Item
            </button>
            <button
              className="custom-appender-btn new-top-content"
              onClick={() => addTopContentBlock()}
            >
              Add Top Content
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const Edit = (props) => {
  const { blocks } = props;
  const Component = blocks.length > 0 ? EditContainer : Placeholder;

  return <Component {...props} />;
};

export default withSelect((select, { clientId }) => {
  const { getBlocks } = select('core/block-editor');
  const blocks = getBlocks(clientId);

  return { blocks };
})(Edit);
